/**
 * Delivery Trigger — drives the hidden "Krever levering" BSS PO switch.
 *
 * When a triggering option value (Print, Send i Post, etc.) is selected,
 * the hidden switch is set to "Ja" → BSS PO adds the delivery addon.
 * When no triggering value is selected, switch is set to "Nei" → BSS PO
 * removes the delivery addon.
 *
 * BSS PO owns all cart logic. This script only controls the hidden switch.
 */

// ============================================================
// CONFIGURATION
// ============================================================
const DELIVERY_TRIGGER = {
  /**
   * Label text used to locate the "Krever levering" option in copied sets.
   */
  optionLabelText: "Krever levering",

  /**
   * Safe fallbacks (old option set) if runtime detection cannot resolve.
   */
  fallbackSwitchName: "po_switch1257498",
  fallbackStackId: "po_stack1257498",

  /** Value to set when delivery is needed. */
  activeValue: "Ja",

  /** Value to set when delivery is not needed. */
  inactiveValue: "Nei",

  /**
   * Values that trigger delivery when selected in a VISIBLE BSS PO option group.
   * Only visible inputs are checked — BSS PO hides conditional groups via
   * display:none, so we walk ancestors looking for that specifically.
   *
   * "Ved post" is intentionally excluded: in the inbound (levering) group it
   * means the customer ships TO oslofoto — no outgoing delivery needed.
   * The retur groups use "Send i Post" / "Hent i Butikk", not "Ved post".
   */
  triggerValues: ["Send i Post", "Print", "Print + Digitale Bilder"],

  /**
   * Normalized keyword matches for return-by-post variants.
   * Examples handled: "Send i post", "Send i posten (uten sporing)",
   * "Retur ... Send i post", "Ja - send i post".
   */
  sendByPostKeywords: ["send i post", "send i posten"],

  /** Inbound-only choices that must never trigger outgoing delivery. */
  nonTriggerKeywords: ["ved post"],

  debug: true,
};
// ============================================================

let initialized = false;
let resolvedSwitchName = "";
let resolvedSwitchContainer = null;
let containerStyleObserver = null;

function log(...args) {
  if (DELIVERY_TRIGGER.debug) console.log("[DeliveryTrigger]", ...args);
}

function normalizeText(value) {
  return (value || "").toLowerCase().replace(/\s+/g, " ").trim();
}

function getTriggerMatch(value) {
  const normalized = normalizeText(value);
  if (!normalized) return null;

  if (
    DELIVERY_TRIGGER.nonTriggerKeywords.some((keyword) =>
      normalized.includes(normalizeText(keyword)),
    )
  ) {
    return null;
  }

  if (
    DELIVERY_TRIGGER.sendByPostKeywords.some((keyword) =>
      normalized.includes(normalizeText(keyword)),
    )
  ) {
    return "send-by-post";
  }

  for (const configuredTrigger of DELIVERY_TRIGGER.triggerValues) {
    const normalizedTrigger = normalizeText(configuredTrigger);
    if (!normalizedTrigger || normalizedTrigger === "send i post") continue;
    if (normalized.includes(normalizedTrigger)) {
      return `configured:${configuredTrigger}`;
    }
  }

  return null;
}

function hideResolvedSwitchContainer() {
  if (!(resolvedSwitchContainer instanceof Element)) return;
  // Keep option logically present for BSS/checkout, but visually hidden on page.
  resolvedSwitchContainer.classList.add("delivery-trigger-visually-hidden");
  resolvedSwitchContainer.setAttribute("aria-hidden", "true");
}

function ensureVisualHideStyles() {
  if (document.getElementById("delivery-trigger-visual-hide-style")) return;
  const style = document.createElement("style");
  style.id = "delivery-trigger-visual-hide-style";
  style.textContent = `
    .delivery-trigger-visually-hidden {
      position: absolute !important;
      left: -9999px !important;
      top: auto !important;
      width: 1px !important;
      height: 1px !important;
      overflow: hidden !important;
      opacity: 0 !important;
      pointer-events: none !important;
    }
  `;
  document.head.appendChild(style);
}

function startContainerStyleObserver() {
  if (!(resolvedSwitchContainer instanceof Element)) return;
  if (containerStyleObserver) {
    containerStyleObserver.disconnect();
    containerStyleObserver = null;
  }

  containerStyleObserver = new MutationObserver(() => {
    if (
      !(resolvedSwitchContainer instanceof Element) ||
      !resolvedSwitchContainer.isConnected
    )
      return;
    // BSS may rewrite inline styles during rerender; force-hide again.
    if (
      !resolvedSwitchContainer.classList.contains(
        "delivery-trigger-visually-hidden",
      )
    ) {
      log("Re-applying hidden state to Krever levering container.");
    }
    hideResolvedSwitchContainer();
  });

  containerStyleObserver.observe(resolvedSwitchContainer, {
    attributes: true,
    attributeFilter: ["style", "class"],
  });
}

function detectSwitchFromStackLabel() {
  const label = normalizeText(DELIVERY_TRIGGER.optionLabelText);
  if (!label) return null;

  const stacks = document.querySelectorAll('[id^="po_stack"]');
  let bestCandidate = null;
  let bestScore = -1;

  for (const stack of stacks) {
    const text = normalizeText(stack.textContent);
    if (!text.includes(label)) continue;
    const switchInput = stack.querySelector('[name^="po_switch"]');
    if (!switchInput) continue;

    // Prefer active/visible stack when duplicated copied option sets exist.
    const score =
      (isBssControlParticipating(stack) ? 2 : 0) +
      (stack.classList.contains("delivery-trigger-visually-hidden") ? 0 : 1) +
      (switchInput.checked ? 1 : 0);

    if (score <= bestScore) continue;
    bestScore = score;
    bestCandidate = {
      switchName: switchInput.name,
      container: stack,
      source: "stack-label-preferred",
    };
  }

  return bestCandidate;
}

function resolveSwitchReferences(reason) {
  const detected = detectSwitchFromStackLabel();
  if (detected?.switchName) {
    resolvedSwitchName = detected.switchName;
    resolvedSwitchContainer = detected.container || null;
    hideResolvedSwitchContainer();
    startContainerStyleObserver();
    log(
      "Resolved switch from label:",
      resolvedSwitchName,
      "| source:",
      detected.source,
      "| reason:",
      reason,
    );
    return;
  }

  const fallbackSwitchInput = document.querySelector(
    `[name="${DELIVERY_TRIGGER.fallbackSwitchName}"]`,
  );
  const fallbackContainer = document.getElementById(
    DELIVERY_TRIGGER.fallbackStackId,
  );
  if (fallbackSwitchInput || fallbackContainer) {
    resolvedSwitchName = DELIVERY_TRIGGER.fallbackSwitchName;
    resolvedSwitchContainer =
      fallbackContainer ||
      fallbackSwitchInput?.closest('[id^="po_stack"]') ||
      null;
    hideResolvedSwitchContainer();
    startContainerStyleObserver();
    log("Using fallback switch:", resolvedSwitchName, "| reason:", reason);
    return;
  }
  //
  resolvedSwitchName = "";
  resolvedSwitchContainer = null;
  if (containerStyleObserver) {
    containerStyleObserver.disconnect();
    containerStyleObserver = null;
  }
  log("Could not resolve Krever levering switch yet.", "| reason:", reason);
}

function getResolvedSwitchName() {
  return resolvedSwitchName || DELIVERY_TRIGGER.fallbackSwitchName;
}

/**
 * True if this BSS control should count toward trigger detection (not in a
 * hidden/disconnected branch of the option UI).
 * Walks ancestors checking for display:none only — the mechanism BSS PO uses
 * to hide conditional groups. Avoids offsetParent (Firefox quirk with
 * position:fixed ancestors) and checkVisibility (too strict with opacity/
 * visibility, falsely filters visible inputs).
 */
function isBssControlParticipating(el) {
  if (!(el instanceof Element) || !el.isConnected) return false;
  let node = el.parentElement;
  while (node && node !== document.body) {
    if (getComputedStyle(node).display === "none") return false;
    node = node.parentElement;
  }
  return true;
}

/**
 * Returns true if any VISIBLE BSS PO option input has a trigger value selected.
 * Skips hidden conditional groups so defaults in collapsed UI do not count.
 */
function isPageTriggerActive() {
  const switchName = getResolvedSwitchName();

  const checked = document.querySelectorAll(
    '[name^="po_button"]:checked, [name^="po_radio"]:checked',
  );
  for (const input of checked) {
    if (input.name === switchName) continue;
    if (!isBssControlParticipating(input)) continue;
    const matchType = getTriggerMatch(input.value);
    if (matchType) {
      log("  Trigger active:", input.name, "=", input.value, "| match:", matchType);
      return true;
    }
  }

  const selects = document.querySelectorAll('[name^="po_dropdown"]');
  for (const sel of selects) {
    if (!isBssControlParticipating(sel)) continue;
    const matchType = getTriggerMatch(sel.value);
    if (matchType) {
      log(
        "  Trigger active (dropdown):",
        sel.name,
        "=",
        sel.value,
        "| match:",
        matchType,
      );
      return true;
    }
  }

  return false;
}

/**
 * Set the hidden BSS PO switch to the correct state.
 * Sets checked directly then dispatches change so BSS PO's onchange fires
 * even though the element is hidden via CSS.
 */
function setSwitchState(active) {
  const switchName = getResolvedSwitchName();
  const targetValue = active
    ? DELIVERY_TRIGGER.activeValue
    : DELIVERY_TRIGGER.inactiveValue;
  const currentInput = document.querySelector(`[name="${switchName}"]:checked`);
  const targetInput = document.querySelector(
    `[name="${switchName}"][value="${targetValue}"]`,
  );

  log(
    "setSwitchState →",
    targetValue,
    "| currentChecked:",
    currentInput?.value || "none",
    "| targetFound:",
    !!targetInput,
  );

  if (!targetInput) {
    // Normal on non-product pages — switch only exists on product pages
    log(
      "Switch not found on this page (expected on cart/collection pages).",
      "| switchName:",
      switchName,
    );
    return;
  }

  if (targetInput.checked) {
    log("Switch already set to:", targetValue);
    return;
  }

  // Uncheck everything in this switch group first
  document.querySelectorAll(`[name="${switchName}"]`).forEach((el) => {
    el.checked = false;
  });

  // Check the target and fire BSS PO's own handler via change event
  targetInput.checked = true;
  targetInput.dispatchEvent(new Event("change", { bubbles: true }));
  log("✓ Switch set to:", targetValue);
}

function syncSwitch(reason) {
  resolveSwitchReferences("sync:" + reason);
  log("Sync triggered by:", reason);
  const active = isPageTriggerActive();
  log("  isPageTriggerActive:", active);
  setSwitchState(active);
}

function initialize() {
  if (initialized) return;
  initialized = true;
  ensureVisualHideStyles();

  resolveSwitchReferences("initialize");
  const switchInputs = document.querySelectorAll(
    `[name="${getResolvedSwitchName()}"]`,
  );
  log(
    "Initialized. Hidden switch inputs found:",
    switchInputs.length,
    "| switchName:",
    getResolvedSwitchName(),
  );

  // React to any BSS PO visible option change
  document.addEventListener("change", (e) => {
    const name = e.target.getAttribute("name") || "";
    const switchName = getResolvedSwitchName();
    // Ignore changes on our own hidden switch to avoid loops
    if (name === switchName) return;
    if (/^po_(button|radio|dropdown)/.test(name)) {
      syncSwitch("change:" + name);
    }
  });

  // BSS often re-renders option nodes after selection changes.
  document.addEventListener("gpoRenderCompleted", () => {
    syncSwitch("gpoRenderCompleted");
  });

  // Also catch BSS PO click-driven changes (programmatic checked state)
  let clickDebounce = null;
  document.addEventListener("click", (e) => {
    const name = e.target.getAttribute("name") || "";
    if (name === getResolvedSwitchName()) return; // ignore our own switch
    clearTimeout(clickDebounce);
    clickDebounce = setTimeout(() => syncSwitch("click"), 200);
  });

  // Initial sync
  syncSwitch("init");
}

function waitForBssPo(callback) {
  resolveSwitchReferences("waitForBssPo:start");
  if (resolvedSwitchName) {
    log("Hidden switch already present.");
    callback();
    return;
  }
  // Also init if any BSS PO inputs are already rendered (switch may appear slightly later)
  if (
    document.querySelector(
      '[name^="po_button"], [name^="po_radio"], [name^="po_dropdown"], [name^="po_switch"]',
    )
  ) {
    log("BSS PO inputs already present, initializing.");
    callback();
    return;
  }
  log("Waiting for BSS PO to render…");
  let called = false;
  const observer = new MutationObserver(() => {
    if (called) return;
    // Trigger as soon as ANY BSS PO option input appears (switch may render slightly after)
    if (
      document.querySelector(
        '[name^="po_button"], [name^="po_radio"], [name^="po_dropdown"], [name^="po_switch"]',
      )
    ) {
      called = true;
      observer.disconnect();
      log("BSS PO inputs detected in DOM.");
      callback();
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });
  // Fallback timeout — skip on non-product pages
  setTimeout(() => {
    if (called) return;
    called = true;
    observer.disconnect();
    const hasBssPo = document.querySelector(
      '[name^="po_button"], [name^="po_radio"], [name^="po_dropdown"], [name^="po_switch"]',
    );
    if (hasBssPo) {
      log("Timeout — BSS PO found, initializing.");
      callback();
    } else {
      log("Timeout — no BSS PO inputs on this page, skipping.");
    }
  }, 8000);
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => waitForBssPo(initialize));
} else {
  waitForBssPo(initialize);
}
